import { PermissionsKey } from '@vedalib/crud'
import { config } from '_appConfig'
import { saveAs } from 'file-saver'
import { FileTypeEnum } from 'utils'

import FileDeleteModal from 'components/modals/FileDeleteModal'
import FileFinderEdit from 'components/modals/FileFinderEdit'
import FileMove from 'components/modals/FileMove'
import AddFolderModal from 'components/modals/FolderAddModal'
import FolderFinderEdit from 'components/modals/FolderFinderEdit'
import ProjectImportModal from 'components/modals/ProjectImport/ProjectImportModal'
import RiseImportModal from 'components/modals/RiseImport'
import type { IMenuOption } from 'components/uiKit/Menu'
import { useOpenModal } from 'components/uiKit/Modal'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { getDataGrouping } from 'components/uiKit/Table/utils'
import { FileMetaFor } from 'gql/__generated__/graphql'
import { useCompanyGetById } from 'gql/companies/apollo'
import { useFileMetasReplaceUsages, useFinderForceDeleteGroupAll } from 'gql/files/apollo'
import { removeFileMetasFromCache } from 'gql/files/cache'
import { useIsFeatureEnabled } from 'services/CompanyFeatures'
import { usePermissions } from 'services/Permission/PermissionContext'
import { t } from 'services/Translation'
import type { FinderItem } from 'store/models'
import { getEnumOption } from 'utils/enum'
import { getFinderFileType } from 'utils/useFinder'
import type { IParams } from 'utils/useFinder'
import { isFinderFile, isFinderFolder } from 'utils/useFinder'

import { FinderTabsEnum } from './FinderFilePicker/types'
import { useFilePickerModal } from './FinderModalPage'
import FinderReplaceConfirmationModal from './FinderReplaceConfirmationModal/FinderReplaceConfirmationModal'
import { useFileReplaceModal } from './FinderReplaceModal'
import { SelectedLabel } from './SelectedLabel'
import { FinderPageActionUiEnum, FinderTableActionUiEnum } from './types'

const isZipFile = (item: FinderItem): boolean => {
  if (!isFinderFile(item)) {
    return false
  }
  const name = item.name?.toLowerCase() || ''
  return name.endsWith('.zip')
}

const allSelectedAreZipFiles = (selected: FinderItem[]): boolean => {
  return selected.length > 0 && selected.every(isZipFile)
}

const getPageActions = (disableUpload?: boolean, isProject?: boolean, library?: boolean | null) => [
  getEnumOption('FinderPageActionUiEnum', FinderPageActionUiEnum.ADD_FOLDER, {
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_c : PermissionsKey.company_resource_c,
    },
  }),
  getEnumOption('FinderPageActionUiEnum', FinderPageActionUiEnum.ADD_FILES, {
    disabled: disableUpload,
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_c : PermissionsKey.company_resource_c,
    },
  }),
  getEnumOption('FinderPageActionUiEnum', FinderPageActionUiEnum.ADD_FROM_LIBRARY, {
    hidden: Boolean(library),
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_c : PermissionsKey.company_resource_c,
    },
  }),
]

export const usePageMenu = (
  params: IParams,
  setShowUploader: () => void,
  disableUpload?: boolean,
  createdFor: FileMetaFor.companies | FileMetaFor.projects = FileMetaFor.companies,
  hideExternalImages?: boolean | null,
) => {
  const addFolderModal = useOpenModal(AddFolderModal, {
    createdFor,
    fileMetaGroupId: params.fileMetaGroupId,
    companyId: params.companyId,
    projectId: params.projectId,
  })

  const filePickModal = useFilePickerModal({
    defaultContentType: FileTypeEnum.IMAGE,
    tabs: [FinderTabsEnum.unsplash],
  })

  const pageActions = usePermissions(
    getPageActions(disableUpload, createdFor === FileMetaFor.projects, hideExternalImages),
  )

  const onClickPageMenu = ({ value }: IMenuOption<FinderPageActionUiEnum>) => {
    if (value === FinderPageActionUiEnum.ADD_FILES) {
      return setShowUploader()
    }

    if (value === FinderPageActionUiEnum.ADD_FOLDER) {
      addFolderModal.open()
    }

    if (value === FinderPageActionUiEnum.ADD_FROM_LIBRARY) {
      filePickModal.open({})
    }
  }

  return { pageActions, onClickPageMenu }
}

const getTableActions = (
  selected: FinderItem[],
  isProject: boolean,
  isModal?: boolean,
  isReplaceEnabled?: boolean,
) => [
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.DOWNLOAD, {
    hidden: selected.length !== 1 || !isFinderFile(selected[0]),
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_r : PermissionsKey.company_resource_r,
    },
  }),
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.UPDATE_FILE, {
    hidden: selected.length !== 1 || !isFinderFile(selected[0]),
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_u : PermissionsKey.company_resource_u,
    },
  }),
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.UPDATE_FOLDER, {
    hidden: selected.length !== 1 || !isFinderFolder(selected[0]),
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_u : PermissionsKey.company_resource_u,
    },
  }),
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.REPLACE, {
    tooltip: !isReplaceEnabled ? t('page.finder.replace.tooltip') : undefined,
    disabled: !isReplaceEnabled,
    hidden:
      selected.length !== 1 ||
      !isFinderFile(selected[0]) ||
      isModal ||
      (selected.length === 1 && selected[0].usageCount === 0),
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_u : PermissionsKey.company_resource_u,
    },
  }),
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.MOVE, {
    hidden: selected.length === 0,
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_u : PermissionsKey.company_resource_u,
    },
  }),
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.IMPORT_PROJECT, {
    hidden: !allSelectedAreZipFiles(selected) || isProject,
    permission: {
      hidden: PermissionsKey.project_import,
    },
  }),
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.IMPORT_RISE, {
    hidden: !allSelectedAreZipFiles(selected) || isProject,
    permission: {
      hidden: PermissionsKey.project_import,
    },
  }),
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.DELETE, {
    hidden: selected.length === 0,
    permission: {
      hidden: isProject ? PermissionsKey.project_resource_d : PermissionsKey.company_resource_d,
    },
  }),
  // TODO: think about refactoring this or simplify
  getEnumOption('FinderTableActionUiEnum', FinderTableActionUiEnum.RESET, {
    label: <SelectedLabel selected={selected} />,
  }),
]

export const useTableMenu = (
  selected: FinderItem[],
  params: IParams,
  createdFor: FileMetaFor.companies | FileMetaFor.projects,
  setSelected: (selected: FinderItem[]) => void,
  isModal?: boolean,
) => {
  const { data } = useCompanyGetById(params.companyId)
  const isReplaceEnabled = useIsFeatureEnabled(data?.data?.companyFeatures, 'DAM_ENABLED')
  const folderFinderEdit = useOpenModal(FolderFinderEdit, { companyId: params.companyId })
  const fileFinderEdit = useOpenModal(FileFinderEdit, {})
  const [forceDeleteById] = useFinderForceDeleteGroupAll()
  const [replace] = useFileMetasReplaceUsages()
  const confirmDeleteModal = useOpenModal(FileDeleteModal, {})
  const canMoveResources = usePermissions([
    PermissionsKey.project_resource_u,
    PermissionsKey.company_resource_u,
  ])
  const displayTab = createdFor === FileMetaFor.projects && canMoveResources

  const moveModal = useOpenModal(FileMove, {
    companyId: params.companyId,
    projectId: params?.projectId || '',
    fileMetaGroupId: params.fileMetaGroupId || '',
    createdFor,
    movingItems: selected,
    displayTab,
  })
  const importModal = useOpenModal(ProjectImportModal, {
    companyId: params.companyId,
    categoryId: 'root',
  })
  const riseImportModal = useOpenModal(RiseImportModal, {
    companyId: params.companyId,
    categoryId: 'root',
  })
  const replaceModal = useFileReplaceModal({
    sourceFileMetaId: selected[0]?.id,
    defaultContentType: getFinderFileType(selected[0]),
  })

  const replaceConfirmationModal = useOpenModal(FinderReplaceConfirmationModal, {})

  const tableActions = usePermissions(
    getTableActions(selected, createdFor === FileMetaFor.projects, isModal, isReplaceEnabled),
  )

  const onClickTableMenu = async ({ value }: IMenuOption<FinderTableActionUiEnum>) => {
    if (value === FinderTableActionUiEnum.UPDATE_FILE) {
      fileFinderEdit.open({
        editableFile: selected[0].id,
        name: selected[0].name,
        type: selected[0].type,
      })
    }

    if (value === FinderTableActionUiEnum.UPDATE_FOLDER) {
      if (isFinderFolder(selected[0])) {
        folderFinderEdit.open({
          editableCategory: selected[0].id,
          companyId: params.companyId,
          color: selected[0].color,
          name: selected[0].name,
        })
      }
    }

    if (value === FinderTableActionUiEnum.REPLACE) {
      const { fileUsageIds, targetFileMetaId } = (await replaceModal.open({})) || {}
      if (!(fileUsageIds && targetFileMetaId)) {
        return
      }

      const confirmed = await replaceConfirmationModal.open({
        sourceFileId: selected[0].id,
        targetFileId: targetFileMetaId,
      })
      if (confirmed) {
        const result = await replace({
          variables: {
            payload: {
              sourceFileMetaId: selected[0].id,
              fileUsageIds,
              targetFileMetaId,
            },
          },
        })

        if (result.data?.data) {
          notify({
            type: NotificationType.success,
            message: t('modal.replaceFile.successNotification'),
          })
        }
      }
    }

    if (value === FinderTableActionUiEnum.MOVE) {
      moveModal.open()
    }

    if (value === FinderTableActionUiEnum.IMPORT_PROJECT) {
      const fileMetaIds = selected.filter(isFinderFile).map((item) => item.id)
      if (fileMetaIds.length > 0) {
        importModal.open({ fileMetaIds })
      }
    }

    if (value === FinderTableActionUiEnum.IMPORT_RISE) {
      const fileMetaIds = selected.filter(isFinderFile).map((item) => item.id)
      if (fileMetaIds.length > 0) {
        riseImportModal.open({ fileMetaIds })
      }
    }

    if (value === FinderTableActionUiEnum.DOWNLOAD) {
      if (isFinderFile(selected[0])) {
        const { files, name } = selected[0]
        saveAs(`${config.io.fileEndpoint}/download/${files?.[0]?.id}`, name?.toString())
      }
    }

    if (value === FinderTableActionUiEnum.DELETE) {
      const data = getDataGrouping(selected)
      const fileDeleteIds = await confirmDeleteModal.open({
        fileIds: data.elementIds || [],
        groupIds: data.groupIds || [],
        companyId: params.companyId,
        projectId: params.projectId,
        createFor: createdFor,
      })
      if (fileDeleteIds) {
        try {
          const result = await forceDeleteById({
            variables: {
              data,
              companyId: params.companyId,
            },
            update: removeFileMetasFromCache(fileDeleteIds),
          })

          if (result.data) {
            notify({
              type: NotificationType.success,
              message: t('notify.delete', { count: selected.length }),
            })
          }
        } catch (err) {
          notify({ type: NotificationType.warning, message: t('notify.cantDelete') })
          console.error(err)
        }
      }
    }
    if (value === FinderTableActionUiEnum.RESET) {
      setSelected([])
    }
  }

  return { tableActions, onClickTableMenu }
}
